/**
 * RecoveryAction (CAP-6, ux-foundations §2). Every failure — backend
 * problem+json, network failure or Firebase auth error — is normalised into
 * one structure a screen can render with RecoveryView. Unknown or missing
 * actions (and any 5xx without one) become `contact_support` with the
 * correlation ID, so no error ever ends without a next step.
 */

export const KNOWN_RECOVERY_ACTIONS = [
  "retry",
  "retry_with_delay",
  "reauthenticate",
  "contact_support",
  "update_payment",
  "switch_payment_method",
  "check_order_status",
  "upload_documents",
  "update_item_details",
  "verify_identity",
  "re_fetch_offers",
  /** Go to sign-in (e.g. `account_exists`). */
  "sign_in",
  /** The OTP is dead (locked/expired/used): send a new one. */
  "resend_code",
] as const;

export type RecoveryActionName = (typeof KNOWN_RECOVERY_ACTIONS)[number];

export interface RecoveryAction {
  errorCode: string;
  kind: "recoverable" | "nonRecoverable";
  action: RecoveryActionName;
  /** i18n key: `errors.<errorCode>`, falling back to `errors.generic`. */
  messageKey: string;
  impactKey?: string;
  deepLink?: string;
  delayMs?: number;
  fields?: string[];
  correlationId?: string;
}

/** RFC 7807 body as sent by backend-app's error handler. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  correlationId?: string;
  errorCode?: string;
  recoveryAction?: {
    action?: string;
    deepLink?: string;
    delayMs?: number;
    fields?: string[];
  };
}

/** A non-2xx API response. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem?: ProblemDetails;
  readonly correlationId?: string;

  constructor(status: number, problem?: ProblemDetails, correlationId?: string) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    this.correlationId = problem?.correlationId ?? correlationId;
  }
}

/** A request that never got a response (offline, DNS, CORS, timeout). */
export class NetworkError extends Error {
  readonly timedOut: boolean;
  constructor(cause: unknown, timedOut = false) {
    super(timedOut ? "Request timed out" : "Network request failed");
    this.name = "NetworkError";
    this.timedOut = timedOut;
    this.cause = cause;
  }
}

export interface ToRecoveryOptions {
  /** Whether a translation key exists (i18next `exists`). */
  hasMessage?: (key: string) => boolean;
}

const GENERIC_MESSAGE = "errors.generic";

function isKnownAction(a: unknown): a is RecoveryActionName {
  return typeof a === "string" && (KNOWN_RECOVERY_ACTIONS as readonly string[]).includes(a);
}

function messageKeyFor(errorCode: string, options?: ToRecoveryOptions): string {
  const key = `errors.${errorCode}`;
  if (!options?.hasMessage) return key;
  return options.hasMessage(key) ? key : GENERIC_MESSAGE;
}

function build(
  errorCode: string,
  action: RecoveryActionName,
  options: ToRecoveryOptions | undefined,
  extra: Partial<RecoveryAction> = {},
): RecoveryAction {
  return {
    errorCode,
    action,
    kind: action === "contact_support" ? "nonRecoverable" : "recoverable",
    messageKey: messageKeyFor(errorCode, options),
    ...extra,
  };
}

/** Firebase Auth error codes → recovery. Unlisted codes → contact_support. */
const FIREBASE_ERRORS: Record<string, { errorCode: string; action: RecoveryActionName; fields?: string[]; delayMs?: number }> = {
  "auth/invalid-verification-code": { errorCode: "auth_invalid_otp", action: "retry", fields: ["otp"] },
  "auth/invalid-credential": { errorCode: "auth_invalid_credential", action: "retry", fields: ["password"] },
  "auth/wrong-password": { errorCode: "auth_invalid_credential", action: "retry", fields: ["password"] },
  "auth/invalid-email": { errorCode: "auth_invalid_email", action: "retry", fields: ["email"] },
  "auth/missing-password": { errorCode: "auth_invalid_credential", action: "retry", fields: ["password"] },
  "auth/too-many-requests": { errorCode: "auth_too_many_requests", action: "retry_with_delay", delayMs: 30_000 },
  "auth/network-request-failed": { errorCode: "network_error", action: "retry" },
  "auth/popup-blocked": { errorCode: "auth_popup_blocked", action: "retry" },
  "auth/user-disabled": { errorCode: "auth_user_disabled", action: "contact_support" },
  "auth/requires-recent-login": { errorCode: "auth_reauthenticate", action: "reauthenticate" },
  "auth/email-already-in-use": { errorCode: "auth_email_in_use", action: "sign_in", fields: ["email"] },
  "auth/weak-password": { errorCode: "auth_weak_password", action: "retry", fields: ["password"] },
};

function firebaseCode(err: unknown): string | null {
  if (typeof err !== "object" || err === null) return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" && code.startsWith("auth/") ? code : null;
}

export function toRecoveryAction(err: unknown, options?: ToRecoveryOptions): RecoveryAction {
  if (err instanceof ApiError) {
    const p = err.problem ?? {};
    const errorCode = p.errorCode ?? `http_${err.status}`;
    const correlationId = err.correlationId;
    const ra = p.recoveryAction;
    const extra: Partial<RecoveryAction> = { correlationId };
    if (ra?.deepLink) extra.deepLink = ra.deepLink;
    if (ra?.delayMs !== undefined) extra.delayMs = ra.delayMs;
    if (ra?.fields) extra.fields = ra.fields;

    if (ra?.action !== undefined) {
      // A backend action we don't know → contact_support, never a dead end.
      return build(errorCode, isKnownAction(ra.action) ? ra.action : "contact_support", options, extra);
    }
    if (err.status >= 500) return build(errorCode, "contact_support", options, extra);
    if (err.status === 401) return build(errorCode, "reauthenticate", options, extra);
    if (err.status === 429) {
      return build(errorCode, "retry_with_delay", options, { delayMs: 30_000, ...extra });
    }
    return build(errorCode, "retry", options, extra);
  }

  if (err instanceof NetworkError) {
    return build(err.timedOut ? "timeout" : "network_error", "retry", options, {
      impactKey: "errors.impact.notSent",
    });
  }

  const code = firebaseCode(err);
  if (code) {
    const mapped = FIREBASE_ERRORS[code];
    if (mapped) {
      const { errorCode, action, ...rest } = mapped;
      return build(errorCode, action, options, rest);
    }
    return build("auth_unknown", "contact_support", options);
  }

  if (err instanceof TypeError || (err instanceof Error && err.name === "AbortError")) {
    return build(err.name === "AbortError" ? "timeout" : "network_error", "retry", options, {
      impactKey: "errors.impact.notSent",
    });
  }

  return build("unknown", "contact_support", options);
}
